import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Smile, Gift as GiftIcon, Users, Rocket, MoreVertical, Sparkles, Star, ChevronLeft, ChevronRight, Hand, Flag, AtSign, Image as ImageIcon, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { UserAvatar, RankBadge } from '../components/RankBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import GiftFly from '../components/GiftFly';
import ReportUserDialog from '../components/ReportUserDialog';
import { toast } from 'sonner';
import { useChannel } from '../lib/ws';
import ws from '../lib/ws';
import { attractivePlanetCount } from '../lib/socialProof';

const CHARACTER_SVGS = [
  'https://api.dicebear.com/7.x/adventurer/svg?seed=Karan&backgroundColor=transparent',
  'https://api.dicebear.com/7.x/adventurer/svg?seed=Deepak&backgroundColor=transparent',
  'https://api.dicebear.com/7.x/adventurer/svg?seed=Sakhad&backgroundColor=transparent',
  'https://api.dicebear.com/7.x/adventurer/svg?seed=Krush&backgroundColor=transparent',
];

// Highlight `@Username` tokens as fuchsia clickable links → /app/users/{uid}.
// The message payload carries `mentions: [user_id]`, but the display name
// itself is embedded in the text as written. We match the longest first so
// "Aurora Nova" wins over "Aurora" if both were tagged.
function renderMessageText(text, mentions) {
  if (typeof text !== 'string' || !text) return text;
  if (!Array.isArray(mentions) || mentions.length === 0) return text;
  // Build a map of displayName → user_id from a lightweight cache attached
  // to the mention list on the sender's side. When only user_ids are
  // available, fall back to matching by any `@word` that appears in text.
  const wanted = [];
  mentions.forEach((entry) => {
    if (typeof entry === 'string') wanted.push({ name: null, user_id: entry });
    else if (entry && entry.displayName) wanted.push({ name: entry.displayName, user_id: entry.user_id });
  });
  const parts = [];
  let i = 0;
  const re = /@([A-Za-z0-9_](?:[A-Za-z0-9_ ]{0,30}[A-Za-z0-9_])?)/g;
  let match;
  while ((match = re.exec(text)) !== null) {
    if (match.index > i) parts.push(text.slice(i, match.index));
    // Match the mention token against known display names (longest first)
    const token = match[1];
    const known = wanted.find(w => w.name && token.startsWith(w.name));
    if (known) {
      parts.push(
        <Link
          key={`m-${match.index}`}
          to={`/app/users/${known.user_id}`}
          className="text-fuchsia-300 font-semibold hover:underline"
          data-testid={`mention-link-${known.user_id}`}
        >
          @{known.name}
        </Link>
      );
      i = match.index + 1 + known.name.length;
    } else if (wanted.length === 1) {
      // Single mention with only user_id — best-effort link the token
      parts.push(
        <Link key={`m-${match.index}`} to={`/app/users/${wanted[0].user_id}`} className="text-fuchsia-300 font-semibold hover:underline">
          @{token}
        </Link>
      );
      i = match.index + 1 + token.length;
    } else {
      parts.push(match[0]);
      i = match.index + match[0].length;
    }
  }
  if (i < text.length) parts.push(text.slice(i));
  return parts;
}

export default function PlanetDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, openBuyStars, creditStars, bumpStats } = useAuth();
  const [planet, setPlanet] = useState(null);
  const [messages, setMessages] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [members, setMembers] = useState([]);
  const [text, setText] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [fly, setFly] = useState(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const scrollRef = useRef(null);

  // Indicates whether the side panels (chat history, members, gifts) are
  // still loading. We DO NOT gate the whole page on these — the planet
  // shell appears as soon as `/planets/{id}` resolves so the user sees
  // the room instantly.
  const [loadingMessages, setLoadingMessages] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoadingMessages(true);
    // Fire 4 requests in parallel but commit state per-response so the
    // user sees the planet shell as soon as it arrives. Previously this
    // used Promise.all which gated EVERYTHING on the slowest (messages,
    // which can be ~2MB if base64 images are present).
    api.get(`/planets/${id}`)
      .then(r => { if (alive) setPlanet(r.data); })
      .catch(() => { if (alive) toast.error('Could not load planet'); });

    api.get(`/planets/${id}/messages`)
      .then(r => { if (alive) setMessages(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoadingMessages(false); });

    api.get('/gifts').then(r => { if (alive) setGifts(r.data); }).catch(() => {});
    api.get('/users/online').then(r => { if (alive) setMembers(r.data); }).catch(() => {});

    // Mark join in the background — never block the UI on it.
    api.post(`/planets/${id}/join`).catch(() => {});

    return () => { alive = false; };
  }, [id]);

  // Live: append new planet messages broadcast over WebSocket. If the echo
  // of our OWN optimistic message arrives first, swap the pending copy for
  // the real one instead of appending a duplicate.
  useChannel(`planet:${id}`, (evt) => {
    if (evt.type !== 'planet.message' || !evt.message) return;
    setMessages(prev => {
      if (prev.some(m => m.id === evt.message.id)) return prev;
      if (user && evt.message.user_id === user.user_id) {
        const idx = prev.findIndex(m => m.pending && m.text === evt.message.text);
        if (idx !== -1) {
          const next = [...prev];
          next[idx] = evt.message;
          return next;
        }
      }
      return [...prev, evt.message];
    });
  }, [id, user?.user_id]);

  // Offline fallback — when the WebSocket is dead (mobile backgrounding,
  // flaky networks), quietly re-pull the latest messages so nothing the
  // user sends or receives silently disappears. Cheap: every 12s, only
  // while the socket is disconnected, and only merges unseen ids.
  useEffect(() => {
    const t = setInterval(async () => {
      if (ws.connected) return;
      try {
        const r = await api.get(`/planets/${id}/messages`);
        setMessages(prev => {
          const seen = new Set(prev.map(m => m.id));
          const merged = [...prev];
          for (const m of r.data) if (!seen.has(m.id)) merged.push(m);
          merged.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          return merged;
        });
      } catch { /* stay silent — next tick retries */ }
    }, 12000);
    return () => clearInterval(t);
  }, [id]);

  // Scroll pinned to the latest message. First mount uses `auto` (instant)
  // so users don't stare at the top while the smooth-scroll travels down;
  // subsequent updates keep the smooth animation for new incoming lines.
  const didInitialScrollRef = useRef(false);
  useEffect(() => { didInitialScrollRef.current = false; }, [id]);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || messages.length === 0) return;
    const behavior = didInitialScrollRef.current ? 'smooth' : 'auto';
    el.scrollTo({ top: el.scrollHeight, behavior });
    didInitialScrollRef.current = true;
    // Images (avatars, base64 payloads) load AFTER paint — re-pin the
    // viewport once every image inside the pane has finished loading so
    // the user isn't stranded mid-thread when the layout shifts.
    const imgs = el.querySelectorAll('img');
    let pending = 0;
    const onLoad = () => {
      pending -= 1;
      if (pending <= 0) el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
    };
    imgs.forEach(img => {
      if (!img.complete) {
        pending += 1;
        img.addEventListener('load', onLoad, { once: true });
        img.addEventListener('error', onLoad, { once: true });
      }
    });
  }, [messages]);

  // @mentions: pending list of picked user_ids to notify. Cleared after send.
  const [mentions, setMentions] = useState([]);   // array of {user_id, displayName}
  // Recipient picker for gifts — falls back to `members[activeIdx]` if empty.
  const [giftRecipient, setGiftRecipient] = useState(null);
  const inputRef = useRef(null);
  const [mentionQuery, setMentionQuery] = useState(null); // null = not active
  const uploadRef = useRef(null);

  const send = async (overrideText, extras = {}) => {
    const body = (overrideText ?? text).trim();
    if (!body) return;
    // Only forward mentions that are still referenced in the text
    const activeMentions = mentions.filter(m => body.includes(`@${m.displayName}`));
    const payload = { text: body };
    if (extras.image_url) payload.image_url = extras.image_url;
    if (activeMentions.length) payload.mentions = activeMentions.map(m => m.user_id);

    // Optimistic: show the message immediately as pending so rapid-fire
    // sends always appear — the real (server) message replaces this temp
    // copy when the POST returns or when the WebSocket echo arrives.
    const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const pendingMsg = {
      id: tempId, planet_id: id, user_id: user.user_id, username: user.displayName,
      avatar: user.avatar, rank: user.rank || 1,
      activity_tier: user.activity_tier || 1, activity_color: user.activity_color || '#94A3B8',
      text: body, system: false, pending: true, created_at: new Date().toISOString(),
      ...(extras.image_url ? { image_url: extras.image_url } : {}),
      ...(activeMentions.length ? { mentions: activeMentions.map(m => m.user_id) } : {}),
    };
    setMessages(prev => [...prev, pendingMsg]);
    if (overrideText === undefined) setText('');
    setMentions([]);
    setMentionQuery(null);

    try {
      const { data } = await api.post(`/planets/${id}/messages`, payload);
      setMessages(prev => {
        const hasReal = prev.some(m => m.id === data.id);
        return prev.map(m => (m.id === tempId ? (hasReal ? null : data) : m)).filter(Boolean);
      });
      // Mirror server rewards optimistically: +5 XP, +1 star, +1 credit per chat
      bumpStats({ xp: 5, stars: 1, credits: 1 });
    } catch (e) {
      // Mark the optimistic copy as failed instead of vanishing — the user
      // sees it went wrong and can tap it to pull the text back into the
      // composer and resend.
      setMessages(prev => prev.map(m => (m.id === tempId ? { ...m, pending: false, failed: true } : m)));
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to send — tap the red message to retry');
      throw e;
    }
  };

  // Tap a failed (red) message → pull its text back into the composer and
  // drop the failed row so the user can send it again.
  const retryFailed = (m) => {
    setMessages(prev => prev.filter(x => x.id !== m.id));
    setText(m.text);
    inputRef.current?.focus();
  };

  const sendWave = async () => {
    try {
      await send('👋');
      toast.success('Wave sent!');
    } catch {
      toast.error('Could not send wave');
    }
  };

  const sendGift = async (gift) => {
    const recipient = giftRecipient || members[activeIdx] || members[0];
    if (!recipient) { toast.error('No recipient online'); return; }
    // Pre-check: warm user-friendly path before the server round-trip
    if ((user?.stars ?? 0) < gift.price) {
      toast.error(`Need ${gift.price - (user?.stars ?? 0)} more ⭐ for ${gift.name}. Top up to send it!`);
      openBuyStars();
      return;
    }
    try {
      await api.post('/gifts/send', { gift_id: gift.id, recipient_id: recipient.user_id, planet_id: id });
      creditStars(-gift.price); // optimistic debit so header updates instantly
      bumpStats({ xp: 10 }); // sender earns +10 XP for gifting
      setFly({ emoji: gift.emoji, name: `${gift.name} → ${recipient.displayName}` });
      toast.success(`Sent ${gift.name} ${gift.emoji}`);
      setGiftRecipient(null);
      // WS will deliver the inline gift chat bubble — no need to refetch
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (e.response?.status === 400 && /not enough stars/i.test(detail)) {
        toast.error(`Not enough stars — open Buy Stars to top up`);
        openBuyStars();
        return;
      }
      toast.error(detail || 'Could not send gift');
    }
  };

  // Detect a live `@query` at the caret so we can surface an autocomplete
  // dropdown of currently-online members. `mentionQuery` is null when the
  // caret isn't inside an `@…` token.
  const handleTextChange = (val) => {
    setText(val);
    // Look for a trailing @token — allow letters/digits/underscore/space
    // but stop at the previous space that isn't right after the `@`.
    const cursor = inputRef.current?.selectionStart ?? val.length;
    const before = val.slice(0, cursor);
    const at = before.lastIndexOf('@');
    if (at < 0) { setMentionQuery(null); return; }
    // must be at start OR preceded by a space
    if (at > 0 && !/\s/.test(before[at - 1])) { setMentionQuery(null); return; }
    const tail = before.slice(at + 1);
    if (/\s{2,}/.test(tail)) { setMentionQuery(null); return; }
    setMentionQuery(tail.toLowerCase());
  };

  const insertMention = (m) => {
    if (!m?.user_id || !m?.displayName) return;
    const cursor = inputRef.current?.selectionStart ?? text.length;
    const before = text.slice(0, cursor);
    const after = text.slice(cursor);
    const at = before.lastIndexOf('@');
    const prefix = at >= 0 ? before.slice(0, at) : before;
    const stitched = `${prefix}@${m.displayName} ${after}`;
    setText(stitched);
    setMentions(prev => (prev.some(x => x.user_id === m.user_id) ? prev : [...prev, m]));
    setMentionQuery(null);
    // Restore focus + caret after mention chip
    setTimeout(() => {
      const el = inputRef.current;
      if (el) {
        const newCursor = prefix.length + m.displayName.length + 2;
        el.focus();
        try { el.setSelectionRange(newCursor, newCursor); } catch { /* noop */ }
      }
    }, 0);
  };

  const openMentionPicker = () => {
    // Manual `@` — jam it into the text and let the autocomplete surface
    const cursor = inputRef.current?.selectionStart ?? text.length;
    const before = text.slice(0, cursor);
    const after = text.slice(cursor);
    const needsSpace = before.length > 0 && !/\s$/.test(before);
    const insert = `${needsSpace ? ' ' : ''}@`;
    const stitched = `${before}${insert}${after}`;
    setText(stitched);
    setMentionQuery('');
    setTimeout(() => {
      const el = inputRef.current;
      if (el) {
        const newCursor = before.length + insert.length;
        el.focus();
        try { el.setSelectionRange(newCursor, newCursor); } catch { /* noop */ }
      }
    }, 0);
  };

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image'); return; }
    try {
      // Resize & compress → /api/uploads/chat → send with image_url reference
      const img = await new Promise((res, rej) => {
        const i = new Image();
        i.onload = () => res(i); i.onerror = () => rej(new Error('Could not read image'));
        i.src = URL.createObjectURL(file);
      });
      const maxDim = 1080;
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      c.getContext('2d').drawImage(img, 0, 0, w, h);
      const blob = await new Promise((res, rej) => c.toBlob(b => b ? res(b) : rej(new Error('Compression failed')), 'image/jpeg', 0.85));
      const fd = new FormData();
      fd.append('file', blob, 'chat.jpg');
      const { data } = await api.post('/uploads/chat', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await send('📷 image', { image_url: data.url });
      toast.success('Image sent');
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Could not send image');
    } finally {
      if (uploadRef.current) uploadRef.current.value = '';
    }
  };

  if (!planet) return (
    <div className="space-y-3 animate-pulse" data-testid="planet-loading">
      <div className="h-32 rounded-2xl bg-white/5" />
      <div className="h-4 w-48 rounded bg-white/10" />
      <div className="h-3 w-72 rounded bg-white/5" />
      <div className="h-[40vh] rounded-2xl bg-white/5" />
    </div>
  );

  const characters = (members.length ? members : []).slice(0, 4);
  const activeMember = characters[activeIdx];

  return (
    <div
      className="-mx-4 lg:-mx-8 -mt-5 -mb-5 lg:-mb-8 flex flex-col h-[calc(100dvh-96px-1.25rem-env(safe-area-inset-bottom))] sm:h-[calc(100dvh-64px-1.25rem-env(safe-area-inset-bottom))]"
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5 bg-[#10101a]">
        <Link to="/app/planets" className="p-1 text-white/70 hover:text-white"><ArrowLeft size={20} /></Link>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-lg truncate flex items-center gap-2">
            <span>{planet.emoji}</span>{planet.name}<span className="text-amber-300">*{Math.floor(planet.members/1000) || 1}{Math.floor((planet.members%1000)/100)}</span>
          </div>
          <div className="text-xs text-white/50" data-testid="planet-detail-online-count">{attractivePlanetCount(planet.online, planet.id).toLocaleString()} online</div>
        </div>
        <Link
          to="/app/planets"
          title="Back to Planets"
          aria-label="Back to Planets"
          data-testid="planet-header-rocket"
          className="p-2 text-white/60 hover:text-cyan-300 transition"
        >
          <Rocket size={18} />
        </Link>
        <button className="p-2 text-white/60 hover:text-white"><MoreVertical size={18} /></button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto scroll-hide px-4 py-4 space-y-3 relative" style={{
        backgroundImage: `radial-gradient(at 20% 30%, rgba(123,47,247,0.08) 0px, transparent 50%), radial-gradient(at 80% 70%, rgba(0,198,255,0.06) 0px, transparent 50%)`,
      }}>
        {loadingMessages && messages.length === 0 && (
          <div className="space-y-3 animate-pulse" data-testid="messages-skeleton">
            {[0,1,2,3].map(i => (
              <div key={i} className={`flex items-start gap-2 ${i % 2 ? 'justify-end' : ''}`}>
                {i % 2 === 0 && <div className="w-8 h-8 rounded-full bg-white/8" />}
                <div className="h-10 w-48 rounded-xl bg-white/8" />
                {i % 2 === 1 && <div className="w-8 h-8 rounded-full bg-white/8" />}
              </div>
            ))}
          </div>
        )}
        {messages.map(m => {
          if (m.system) return (
            <div key={m.id} className="text-center text-xs text-white/40">{m.text}</div>
          );
          if (m.isGift) return (
            <div key={m.id} className="flex items-start gap-2">
              <UserAvatar user={{ avatar: m.avatar, username: m.username, rank: m.rank }} size={32} />
              <div>
                <div className="text-xs text-amber-300 flex items-center gap-1"><span className="font-semibold text-white">{m.username}</span><RankBadge rank={m.rank || 1} /></div>
                <div className="inline-flex items-center gap-2 mt-1 px-3 py-2 rounded-xl text-sm" style={{ background: 'linear-gradient(135deg,rgba(251,191,36,0.15),rgba(244,114,182,0.1))', border: '1px solid rgba(251,191,36,0.3)' }}>
                  <Sparkles size={14} className="text-amber-300" />{m.text}
                </div>
              </div>
            </div>
          );
          const mine = user && m.user_id === user.user_id;
          const canReport = !mine && !m.ai_host && !m.system && m.user_id;
          const canOpenProfile = !!m.user_id && !m.ai_host && !m.system;
          const avatarUser = { avatar: m.avatar, username: m.username, rank: m.rank, avatar_is_ai: !!m.ai_host };
          return (
            <div key={m.id} className="group flex items-start gap-2">
              {canOpenProfile ? (
                <Link to={`/app/users/${m.user_id}`} className="shrink-0" title={`View ${m.username}'s profile`} data-testid={`planet-msg-avatar-${m.id}`}>
                  <UserAvatar user={avatarUser} size={32} />
                </Link>
              ) : (
                <UserAvatar user={avatarUser} size={32} />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {canOpenProfile ? (
                    <Link
                      to={`/app/users/${m.user_id}`}
                      className={`text-sm font-semibold hover:underline ${mine ? 'text-cyan-300' : ''}`}
                      style={!mine && m.activity_color ? { color: m.activity_color } : undefined}
                      data-testid={`planet-msg-user-${m.id}`}
                    >
                      {m.username}
                    </Link>
                  ) : (
                    <span
                      className={`text-sm font-semibold ${mine ? 'text-cyan-300' : ''}`}
                      style={!mine && m.activity_color ? { color: m.activity_color } : undefined}
                    >
                      {m.username}
                    </span>
                  )}
                  {m.ai_host ? (
                    <span
                      className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-500/30"
                      data-testid="ai-host-badge"
                      title="Themed prompt from the planet's AI host"
                    >
                      {m.kind === 'quiz' ? 'AI QUIZ'
                        : m.kind === 'quiz_reveal' ? 'ANSWER'
                        : m.kind === 'poll' ? 'AI POLL'
                        : m.kind === 'fact' ? 'AI FACT'
                        : m.kind === 'challenge' ? 'CHALLENGE'
                        : m.kind === 'hot_take' ? 'HOT TAKE'
                        : m.kind === 'daily_image' ? 'AI ART'
                        : m.kind === 'direct' ? 'AI HOST'
                        : 'AI HOST'}
                    </span>
                  ) : (
                    <RankBadge rank={m.rank || 1} />
                  )}
                  <span className="text-[11px] text-white/40">{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  {canReport && (
                    <button
                      type="button"
                      onClick={() => setReportTarget({ user_id: m.user_id, displayName: m.username, message_id: m.id })}
                      data-testid={`report-message-${m.id}`}
                      className="ml-auto opacity-0 group-hover:opacity-100 focus:opacity-100 transition text-white/40 hover:text-rose-300 p-1 -mr-1"
                      title="Report this user"
                      aria-label="Report this user"
                    >
                      <Flag size={12} />
                    </button>
                  )}
                </div>
                {m.image_url && m.ai_host && (
                  <div className="mt-1.5 max-w-md">
                    <a
                      href={m.image_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="ai-host-image"
                      className="block rounded-2xl overflow-hidden border border-fuchsia-500/20 relative"
                    >
                      <img
                        src={m.image_url.startsWith('http') ? m.image_url : `${process.env.REACT_APP_BACKEND_URL || ''}${m.image_url}`}
                        alt={m.image_prompt || 'AI generated art'}
                        loading="lazy"
                        className="w-full h-auto block"
                      />
                      <span
                        className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-black/70 text-fuchsia-200 border border-fuchsia-400/50 backdrop-blur inline-flex items-center gap-1"
                        data-testid="ai-generated-image-label"
                      >
                        <Sparkles size={10} /> AI generated
                      </span>
                    </a>
                    <div className="mt-1 text-[10px] text-white/45 italic">
                      Image created by AI — may not reflect real places or people.
                    </div>
                  </div>
                )}
                {(() => {
                  const legacyDataImg = typeof m.text === 'string' && m.text.startsWith('data:image/');
                  const userImageSrc = (m.image_url && !m.ai_host)
                    ? (m.image_url.startsWith('http') ? m.image_url : `${process.env.REACT_APP_BACKEND_URL || ''}${m.image_url}`)
                    : (legacyDataImg ? m.text : null);
                  if (userImageSrc) {
                    return (
                      <div className="inline-block mt-1 rounded-xl overflow-hidden p-1 bg-white/8" data-testid={`chat-image-${m.id}`}>
                        <img src={userImageSrc} alt="" loading="lazy" decoding="async" className="rounded-lg max-w-[240px] max-h-[280px] object-cover block" />
                      </div>
                    );
                  }
                  if (m.ai_host && m.image_url) return null; // already rendered above
                  return (
                    <div
                      className={`inline-block mt-1 px-3 py-2 rounded-xl text-sm whitespace-pre-wrap ${
                        m.failed
                          ? 'bg-rose-500/10 border border-rose-500/50 text-rose-200 cursor-pointer'
                          : m.pending
                          ? 'bg-white/5 text-white/60'
                          : m.ai_host
                          ? 'bg-fuchsia-500/8 border border-fuchsia-500/20 text-white/90'
                          : 'bg-white/8 text-white/90'
                      }`}
                      onClick={m.failed ? () => retryFailed(m) : undefined}
                      title={m.failed ? 'Not sent — tap to edit & retry' : m.pending ? 'Sending…' : undefined}
                      data-testid={m.failed ? `failed-msg-${m.id}` : m.pending ? `pending-msg-${m.id}` : undefined}
                    >
                      {renderMessageText(m.text, m.mentions)}
                      {m.pending && <Loader2 size={12} className="inline ml-2 animate-spin text-white/40" />}
                      {m.failed && <span className="block mt-1 text-[10px] uppercase tracking-wider text-rose-300/80 font-semibold">Not sent — tap to retry</span>}
                    </div>
                  );
                })()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      <div
        className="border-t border-white/5 bg-[#10101a] px-3 py-2 flex items-center gap-1 relative shrink-0"
        style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
      >
        <div className="relative">
          <button
            onClick={() => setEmojiOpen(o => !o)}
            data-testid="emoji-picker-btn"
            className={`p-2 transition ${emojiOpen ? 'text-amber-300' : 'text-cyan-400 hover:text-cyan-300'}`}
            aria-label="Insert emoji"
          >
            <Smile size={20} />
          </button>
          {emojiOpen && (
            <>
              <div className="fixed inset-0 z-[40]" onClick={() => setEmojiOpen(false)} />
              <div
                className="absolute bottom-12 left-0 z-[50] w-72 rounded-2xl bg-[#161622] border border-white/10 shadow-2xl p-3"
                data-testid="planet-emoji-panel"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Quick emojis</div>
                <div className="grid grid-cols-8 gap-1 max-h-56 overflow-y-auto scroll-hide">
                  {['😀','😂','🥰','😍','😎','🤩','🥳','😭',
                    '😅','😉','😘','🤗','🤔','😏','😴','🙄',
                    '😱','🥺','😡','😤','🤯','🥶','🤤','🤠',
                    '👍','👎','👏','🙌','🙏','💪','👋','🤝',
                    '❤️','🧡','💛','💚','💙','💜','🖤','🤍',
                    '🔥','✨','⭐','🌟','💫','💥','💯','🎉',
                    '🌹','🌸','🌻','🌈','🎁','🎂','🍕','☕',
                    '🚀','🌍','🌌','🪐','👽','🌙','☀️','⚡',
                  ].map((e, i) => (
                    <button
                      key={i}
                      onClick={() => { setText(t => t + e); setEmojiOpen(false); }}
                      data-testid={`emoji-${e}`}
                      className="text-2xl p-1.5 rounded-lg hover:bg-white/10 transition"
                    >
                      {e}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <button data-testid="planet-gift-btn" className="p-2 text-white/60 hover:text-amber-300"><GiftIcon size={20} /></button>
          </DialogTrigger>
          <DialogContent data-testid="planet-gift-dialog" className="bg-[#161622] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display">Send a gift {giftRecipient ? `to ${giftRecipient.displayName}` : (activeMember ? `to ${activeMember.displayName}` : '')}</DialogTitle>
            </DialogHeader>
            {members.length > 0 && (
              <div className="mt-1">
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Choose a recipient</div>
                <div className="flex gap-1.5 overflow-x-auto scroll-hide pb-1" data-testid="planet-gift-recipients">
                  {members.slice(0, 20).map(m => {
                    const selected = giftRecipient?.user_id === m.user_id || (!giftRecipient && activeMember?.user_id === m.user_id);
                    return (
                      <button
                        key={m.user_id}
                        onClick={() => setGiftRecipient(m)}
                        data-testid={`gift-recipient-${m.user_id}`}
                        className={`shrink-0 px-2.5 py-1 rounded-full border text-[11px] transition ${selected ? 'bg-amber-400/20 border-amber-400/60 text-amber-100' : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'}`}
                      >
                        {m.displayName}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="grid grid-cols-4 gap-2 mt-3 max-h-72 overflow-y-auto scroll-hide">
              {gifts.map(g => (
                <button key={g.id} data-testid={`planet-gift-${g.id}`} onClick={() => sendGift(g)} className="rounded-xl p-3 bg-white/5 hover:bg-white/10 border border-white/5 hover:border-amber-400/30 text-center">
                  <div className="text-3xl mb-1">{g.emoji}</div>
                  <div className="text-[11px] font-semibold truncate">{g.name}</div>
                  <div className="text-[10px] text-amber-300 flex items-center justify-center gap-0.5"><Star size={8} fill="currentColor" />{g.price}</div>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
        {/* Image upload — new endpoint keeps chat threads permanently fast */}
        <input
          ref={uploadRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImagePick}
          data-testid="planet-image-input"
        />
        <button
          onClick={() => uploadRef.current?.click()}
          data-testid="planet-image-btn"
          className="p-2 text-white/60 hover:text-cyan-300"
          title="Attach image"
          aria-label="Attach image"
        >
          <ImageIcon size={20} />
        </button>
        {/* @Mention picker button */}
        <button
          onClick={openMentionPicker}
          data-testid="planet-mention-btn"
          className="p-2 text-white/60 hover:text-fuchsia-300"
          title="Tag someone"
          aria-label="Tag someone in this planet"
        >
          <AtSign size={20} />
        </button>
        {/* @Mention autocomplete panel — sits above the input */}
        {mentionQuery !== null && (() => {
          const matches = (members || [])
            .filter(m => m.user_id && m.user_id !== user?.user_id)
            .filter(m => !mentionQuery || m.displayName?.toLowerCase().includes(mentionQuery))
            .slice(0, 6);
          if (matches.length === 0) return null;
          return (
            <div
              className="absolute bottom-[52px] left-2 right-2 max-w-sm z-[45] rounded-2xl bg-[#161622] border border-fuchsia-500/25 shadow-2xl p-2"
              data-testid="planet-mention-panel"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-[10px] uppercase tracking-wider text-fuchsia-300/80 mb-1 flex items-center gap-1">
                <AtSign size={10} /> Tag a voyager
              </div>
              <div className="flex flex-col max-h-56 overflow-y-auto scroll-hide">
                {matches.map(m => (
                  <button
                    key={m.user_id}
                    onClick={() => insertMention(m)}
                    data-testid={`mention-pick-${m.user_id}`}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/8 text-left"
                  >
                    <UserAvatar user={{ avatar: m.avatar, username: m.displayName, rank: m.rank }} size={24} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{m.displayName}</div>
                      <div className="text-[10px] text-white/40 truncate">{m.country || 'Voyager'}</div>
                    </div>
                    <RankBadge rank={m.rank || 1} />
                  </button>
                ))}
              </div>
            </div>
          );
        })()}
        <Input
          ref={inputRef}
          value={text}
          onChange={e => handleTextChange(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); send(); } }}
          placeholder="Type a message… (type @ to tag)"
          data-testid="planet-text-input"
          className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/40 h-10"
        />
        <button onClick={() => send()} disabled={!text.trim()} data-testid="planet-send-btn" className="p-2 text-cyan-400 disabled:text-white/20"><Send size={20} /></button>
        <button
          onClick={sendWave}
          data-testid="wave-btn"
          className="p-2 text-amber-300 hover:text-amber-200"
          aria-label="Send a wave"
          title="Send a wave"
        >
          <Hand size={20} />
        </button>
      </div>
      {fly && <GiftFly emoji={fly.emoji} name={fly.name} onDone={() => setFly(null)} />}
      <ReportUserDialog
        open={!!reportTarget}
        onOpenChange={(o) => { if (!o) setReportTarget(null); }}
        target={reportTarget}
        context={reportTarget ? { planet_id: id, message_id: reportTarget.message_id } : undefined}
      />
    </div>
  );
}
